import { useEffect } from 'react';

import { useDeleteLeadsGroupsMutation } from '@app/api/Leads';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

interface UseLeadsRegistrySearchIdCleanupParams {
  activeBranchId: number;
  pendingSearchIds: string[];
  onCleaned(searchIds: string[]): void;
}

// Deletes stale registry search groups on the server. When an out-of-order
// `SearchLeads` response is discarded, its `SearchId` is queued here and removed
// via `DeleteLeadsGroups` so the backend drops the abandoned group. Mirrors the
// legacy `deleteLeadsGroupsHandler` queue, owned page-locally by Leads.
export const useLeadsRegistrySearchIdCleanup = ({
  activeBranchId,
  pendingSearchIds,
  onCleaned,
}: UseLeadsRegistrySearchIdCleanupParams) => {
  const { showNotification } = useNotification();
  const [deleteLeadsGroups] = useDeleteLeadsGroupsMutation();

  useEffect(() => {
    if (!activeBranchId || pendingSearchIds.length === 0) return;

    const searchIdsToDelete = pendingSearchIds;

    const cleanupSearchGroups = async () => {
      try {
        await deleteLeadsGroups({
          ActiveBranchId: activeBranchId,
          data: { SearchIds: searchIdsToDelete },
        }).unwrap();

        onCleaned(searchIdsToDelete);
      } catch (error) {
        showNotification(error, 'warning');
      }
    };

    void cleanupSearchGroups();
  }, [
    activeBranchId,
    deleteLeadsGroups,
    onCleaned,
    pendingSearchIds,
    showNotification,
  ]);
};
